import React, { useState } from 'react';
import { AlertTriangleIcon, ArrowLeftIcon, BadgeCheckIcon, ExternalLinkIcon, ShieldAlertIcon } from 'lucide-react';
import { Badge } from '../ui/Badge';
import { Button } from '../ui/Button';
import { Panel } from '../ui/Panel';
import { naira } from '../../utils/format';
import { MODE_LABEL, ORIGIN_EXPLAINER, ORIGIN_LABEL, ORIGIN_TONE, PAYEE_LABEL, feeTotals, orUnavailable } from '../../utils/services';
import { serviceProviders, serviceInstitutions } from '../../data/services';
import type { AcademicService, FulfilmentMode } from '../../types/services';

/**
 * Answers, in order: what, who provides it, where it happens, what you need,
 * what it costs, whether anyone can legitimately help, and what to do now.
 */
export function ServiceDetail({
  service,
  onBack,
  onStart
}: {
  service: AcademicService;
  onBack: () => void;
  onStart: (mode: FulfilmentMode, providerId: string | null) => void;
}) {
  const [mode, setMode] = useState<FulfilmentMode>(service.modes[0]);
  const [providerId, setProviderId] = useState<string | null>(null);
  const institution = serviceInstitutions.find(item => item.id === service.institutionId);
  const totals = feeTotals(service.fees);
  const providers = serviceProviders.filter(provider => provider.serviceIds.includes(service.id) && provider.institutionsServed.includes(service.institutionId));
  const eligibleProviders = providers.filter(provider => provider.status === 'Verified');
  const unverified = providers.filter(provider => provider.status !== 'Verified');
  return <div className="space-y-4" data-id="element-4394" data-mp="q1ohvqogl9pq">
      <div data-id="element-4395" data-mp="q2zjpkycg7e7">
        <Button size="sm" variant="ghost" icon={<ArrowLeftIcon className="h-3.5 w-3.5" data-id="element-4397" data-mp="l2v560gzxmqm" />} onClick={onBack} data-id="element-4396" data-mp="q2hgu5h6euxb">
          All services
        </Button>
      </div>

      <div className="rounded-card border border-line bg-surface px-5 py-5" data-id="element-4398" data-mp="h1rnd8fe1gmr">
        <div className="flex flex-wrap items-center gap-2" data-id="element-4399" data-mp="c26p7081lcb3">
          <Badge tone={ORIGIN_TONE[service.origin]} icon={<BadgeCheckIcon className="h-3.5 w-3.5" data-id="element-4401" data-mp="2et07pnkd8du" />} data-id="element-4400" data-mp="c2otmk4uywu3">
            {ORIGIN_LABEL[service.origin]}
          </Badge>
          <Badge tone="neutral" data-id="element-4402" data-mp="6gekhw2fgoy2">{service.category}</Badge>
          {!service.catalogueVerified && <Badge tone="warn" data-id="element-4403" data-mp="3omaro4ger2aa">Not confirmed with the institution</Badge>}
        </div>
        <h2 className="mt-2.5 text-xl font-semibold tracking-tight text-ink" data-id="element-4404" data-mp="3tkuhify4gy7x">{service.name}</h2>
        <p className="mt-1 text-[13px] text-ink-muted" data-id="element-4405" data-mp="l52brq58qci7">
          {institution?.name}
          {institution?.pilot ? ' · pilot institution' : ''}
        </p>
        <p className="mt-2.5 max-w-2xl text-sm leading-6 text-ink-muted" data-id="element-4406" data-mp="q3m01x7b40dq">{service.description}</p>
        <p className="mt-3 max-w-2xl rounded-lg border border-line bg-canvas px-3.5 py-3 text-[12px] leading-5 text-ink-muted" data-id="element-4407" data-mp="beptk4kwevf8">
          {ORIGIN_EXPLAINER[service.origin]}
        </p>
      </div>

      <div className="grid gap-4 lg:grid-cols-3" data-id="element-4408" data-mp="142bwrbq7ry1k">
        <div className="space-y-4 lg:col-span-2" data-id="element-4409" data-mp="18ud5ino6b6mu">
          <Panel title="Who provides it, and where" data-id="element-4410" data-mp="18w7pnzrmeupv">
            <dl className="grid gap-x-6 gap-y-3 sm:grid-cols-2" data-id="element-4411" data-mp="2tks8qntptgmx">
              {[{
              term: 'Official provider',
              value: orUnavailable(service.officialProvider)
            }, {
              term: 'Where it is requested',
              value: orUnavailable(service.officialMethod)
            }, {
              term: 'Who is eligible',
              value: service.eligibility
            }, {
              term: 'Processing estimate',
              value: orUnavailable(service.processingEstimate)
            }, {
              term: 'Collection',
              value: service.collectionMethods.join(', ')
            }, {
              term: 'Delivery',
              value: service.deliveryOptions.join(', ')
            }, {
              term: 'Catalogue source',
              value: orUnavailable(service.sourceAuthority)
            }, {
              term: 'Last updated',
              value: service.lastUpdated
            }].map(row => <div key={row.term} data-id="element-4412" data-mp="3v1hwen83fwde">
                  <dt className="text-[12px] font-semibold uppercase tracking-wide text-ink-subtle" data-id="element-4413" data-mp="3v0gepzwkobmp">
                    {row.term}
                  </dt>
                  <dd className="mt-0.5 text-[13px] leading-6 text-ink" data-id="element-4414" data-mp="p88nel7u6c2e">{row.value}</dd>
                </div>)}
            </dl>
            {service.officialUrl && <a href={service.officialUrl} target="_blank" rel="noreferrer" className="mt-3 inline-flex items-center gap-1.5 text-[13px] font-medium text-brand-800 hover:underline" data-id="element-4415" data-mp="z5wl9k0qnzos">
                Official institution website <ExternalLinkIcon className="h-3.5 w-3.5" data-id="element-4416" data-mp="184qlrlh6xa7m" />
              </a>}
          </Panel>

          <Panel title="What you need" description="Gather these before you start." data-id="element-4417" data-mp="1hwedlnhnst9m">
            <ul className="divide-y divide-line" data-id="element-4418" data-mp="1hyqqvnch2fgc">
              {service.requirements.map(requirement => <li key={requirement.id} className="flex flex-wrap items-start gap-2 py-2.5 first:pt-0" data-id="element-4419" data-mp="1ruk83k14yx4g">
                  <span className="min-w-0 flex-1" data-id="element-4420" data-mp="1vvw3652s4ak1">
                    <span className="block text-[13px] text-ink" data-id="element-4421" data-mp="1vwp58sqsmbvu">{requirement.label}</span>
                    {requirement.detail && <span className="mt-0.5 block text-[12px] text-ink-muted" data-id="element-4422" data-mp="3ccmj1y1ge61a">{requirement.detail}</span>}
                  </span>
                  <Badge tone={requirement.source === 'institution' ? 'ok' : 'neutral'} data-id="element-4423" data-mp="37e1qmksg0qne">
                    {requirement.source === 'institution' ? 'Required by the university' : requirement.source === 'provider' ? 'Required by the provider' : 'Required by Campuzon'}
                  </Badge>
                  {requirement.optional && <Badge tone="neutral" data-id="element-4424" data-mp="3qajgewnqegh0">Only if applicable</Badge>}
                </li>)}
            </ul>
          </Panel>

          <Panel title="How you want it done" data-id="element-4425" data-mp="mn0y9g8hsiqe">
            <ul className="space-y-2.5" data-id="element-4426" data-mp="movjkvnoecv0">
              {service.modes.map(option => <li key={option} data-id="element-4427" data-mp="hvso2hzc8tc6">
                  <label className={`flex cursor-pointer items-start gap-3 rounded-lg border px-3.5 py-3 transition-colors duration-150 ease-out ${mode === option ? 'border-brand-700 bg-brand-50' : 'border-line-strong hover:border-ink-subtle'}`} data-id="element-4428" data-mp="huhnss53q3pt">
                    <input type="radio" name="fulfilment" checked={mode === option} onChange={() => {
                  setMode(option);
                  if (option !== 'assisted') setProviderId(null);
                }} className="mt-0.5 h-4 w-4 shrink-0 accent-brand-700" data-id="element-4429" data-mp="27d6c9v0tcplu" />
                    <span className="min-w-0" data-id="element-4430" data-mp="1ofvc887cxs5c">
                      <span className="block text-[13px] font-semibold text-ink" data-id="element-4431" data-mp="1ogon9ldcn3ta">
                        {MODE_LABEL[option]}
                      </span>
                      <span className="mt-0.5 block text-[12px] leading-5 text-ink-muted" data-id="element-4432" data-mp="3irjx0r09soxn">
                        {option === 'official_digital' ? `Campuzon shows you the requirements and tracks your own deadline. You lodge it at ${orUnavailable(service.officialMethod).toLowerCase()} and Campuzon never asks for your university login.` : option === 'assisted' ? 'A verified provider handles the parts that need someone physically present. You keep control through a written authorisation.' : 'The university receives this request through Campuzon under a formal agreement, and status updates come back automatically.'}
                      </span>
                    </span>
                  </label>
                </li>)}
            </ul>

            {mode === 'assisted' && <div className="mt-3.5 border-t border-line pt-3.5" data-id="element-4433" data-mp="2m8monsji2mrm">
                <p className="text-[13px] font-semibold text-ink" data-id="element-4434" data-mp="2mafxnehl5j2s">Choose someone to assist you</p>
                {eligibleProviders.length === 0 ? <p className="mt-1.5 text-[13px] text-ink-muted" data-id="element-4435" data-mp="3frkz8hvhfm6x">
                    No verified provider currently offers this service at {institution?.short}. You can
                    still handle it yourself.
                  </p> : <ul className="mt-2.5 space-y-2" data-id="element-4436" data-mp="2rufa8x1ta980">
                    {eligibleProviders.map(provider => <li key={provider.id} data-id="element-4437" data-mp="2wt8o4ql0g28p">
                        <label className={`flex cursor-pointer items-start gap-3 rounded-lg border px-3.5 py-3 transition-colors duration-150 ease-out ${providerId === provider.id ? 'border-brand-700 bg-brand-50' : 'border-line-strong hover:border-ink-subtle'}`} data-id="element-4438" data-mp="2wu1z63r05dwn">
                          <input type="radio" name="provider" checked={providerId === provider.id} onChange={() => setProviderId(provider.id)} className="mt-0.5 h-4 w-4 shrink-0 accent-brand-700" data-id="element-4439" data-mp="l5snbdesary5" />
                          <span className="min-w-0 flex-1" data-id="element-4440" data-mp="hfzw7yg4w4wq">
                            <span className="flex flex-wrap items-center gap-2" data-id="element-4441" data-mp="chy28ih66l6h">
                              <span className="text-[13px] font-semibold text-ink" data-id="element-4442" data-mp="7ibq6cd2vaz9">{provider.name}</span>
                              <Badge tone="ok" icon={<BadgeCheckIcon className="h-3.5 w-3.5" data-id="element-4444" data-mp="2igyw7pe4jdo" />} data-id="element-4443" data-mp="2iz2xqiao1w5">
                                Campuzon verified
                              </Badge>
                            </span>
                            <span className="mt-0.5 block text-[12px] leading-5 text-ink-muted" data-id="element-4445" data-mp="1wxjijorrd78o">
                              {provider.location} · from {naira(provider.feeFrom)} ·{' '}
                              {provider.processingEstimate} · {provider.ratingLabel}
                            </span>
                            <span className="mt-1 block text-[12px] leading-5 text-ink-muted" data-id="element-4446" data-mp="1lhii8l9vus68">
                              {provider.terms}
                            </span>
                            <span className="mt-1 flex items-start gap-1.5 text-[12px] leading-5 text-warn-700" data-id="element-4447" data-mp="1uji3jprxkiez">
                              <ShieldAlertIcon className="mt-0.5 h-3.5 w-3.5 shrink-0" data-id="element-4448" data-mp="16sw5mrbn4eip" />
                              {provider.authorityNote}
                            </span>
                          </span>
                        </label>
                      </li>)}
                  </ul>}

                {unverified.length > 0 && <p className="mt-2.5 rounded-lg border border-line bg-canvas px-3.5 py-2.5 text-[12px] leading-5 text-ink-muted" data-id="element-4449" data-mp="o367pvckzcmk">
                    {unverified.length} other {unverified.length === 1 ? 'applicant' : 'applicants'} for
                    this service {unverified.length === 1 ? 'has' : 'have'} not completed verification and
                    cannot be selected.
                  </p>}
              </div>}
          </Panel>
        </div>

        <div className="space-y-4" data-id="element-4450" data-mp="2loxeb6t06pxr">
          <Panel title="What it costs" data-id="element-4451" data-mp="2prkbln8g132c">
            <ul className="divide-y divide-line" data-id="element-4452" data-mp="2psll3n9t9hhy">
              {service.fees.map(fee => <li key={fee.label} className="flex items-baseline justify-between gap-3 py-2 first:pt-0" data-id="element-4453" data-mp="3dipnxsf6dd7w">
                  <span className="min-w-0" data-id="element-4454" data-mp="3iewwlsyyh2tj">
                    <span className="block text-[13px] text-ink" data-id="element-4455" data-mp="34hrm9gs7lhsh">{fee.label}</span>
                    <span className="block text-[12px] text-ink-subtle" data-id="element-4456" data-mp="39g2s7gfdv85f">{PAYEE_LABEL[fee.payee]}</span>
                  </span>
                  <span className="cz-num shrink-0 text-[13px] font-semibold text-ink" data-id="element-4457" data-mp="1t11jy8qwpa45">
                    {fee.unknown ? 'Not published' : naira(fee.amount)}
                  </span>
                </li>)}
            </ul>
            <div className="mt-2.5 border-t border-line pt-2.5" data-id="element-4458" data-mp="3k63aohjnehp">
              <p className="flex items-baseline justify-between gap-3" data-id="element-4459" data-mp="3usa3leuu7x70">
                <span className="text-[13px] font-semibold text-ink" data-id="element-4460" data-mp="3q00h68sxlf9h">Total shown</span>
                <span className="cz-num text-[15px] font-semibold text-ink" data-id="element-4461" data-mp="3lyx4twa3e1d0">{naira(totals.total)}</span>
              </p>
              <p className="mt-1.5 text-[12px] leading-5 text-ink-muted" data-id="element-4462" data-mp="3h03z4q11rene">
                University fees and service fees are shown separately and never combined. Campuzon does
                not set or collect the university’s fee.
              </p>
              {totals.hasUnknown && <p className="mt-1.5 text-[12px] leading-5 text-warn-700" data-id="element-4463" data-mp="330kpttwi1x9i">
                  At least one official fee has not been published by the institution. Campuzon will not
                  estimate it.
                </p>}
            </div>
          </Panel>

          <Panel title="Limits you should know" data-id="element-4464" data-mp="2j6cem433jj06">
            <ul className="space-y-2" data-id="element-4465" data-mp="2j5jhlutxw7v8">
              {service.restrictions.map(restriction => <li key={restriction} className="flex items-start gap-2 text-[12px] leading-5 text-ink-muted" data-id="element-4466" data-mp="2o44fvopmshh1">
                  <AlertTriangleIcon className="mt-0.5 h-3.5 w-3.5 shrink-0 text-warn-700" data-id="element-4467" data-mp="2swxmki4ntzvr" />
                  {restriction}
                </li>)}
              <li className="flex items-start gap-2 text-[12px] leading-5 text-ink-muted" data-id="element-4468" data-mp="2088kafrvboif">
                <AlertTriangleIcon className="mt-0.5 h-3.5 w-3.5 shrink-0 text-warn-700" data-id="element-4469" data-mp="2561nr11se4f0" />
                Campuzon never asks for your university username or password, and no provider may request
                them.
              </li>
            </ul>
          </Panel>

          <div className="rounded-card border border-line bg-surface px-5 py-4" data-id="element-4470" data-mp="2e4vj6o3dsoqz">
            <p className="text-[13px] font-semibold text-ink" data-id="element-4471" data-mp="3kpejufjbm736">Next step</p>
            <p className="mt-1 text-[12px] leading-5 text-ink-muted" data-id="element-4472" data-mp="3pl3p8zi0ui7e">
              {mode === 'assisted' && !providerId ? 'Choose a verified provider to continue.' : mode === 'official_digital' ? 'Campuzon creates a tracked request so you keep the checklist and deadline, then points you at the official channel.' : 'Campuzon submits this to the institution and reports status back here.'}
            </p>
            <Button variant="primary" className="mt-3 w-full" disabled={mode === 'assisted' && !providerId} onClick={() => onStart(mode, providerId)} data-id="element-4473" data-mp="axxuyczpm9bp">
              {mode === 'official_digital' ? 'Track this myself' : 'Start this request'}
            </Button>
          </div>
        </div>
      </div>
    </div>;
}